import { Check } from "lucide-react";
import { CATEGORIES, LEVELS, GOALS, AVAILABILITY, STATIONS, formatTime } from "../../constants/rox";
import { PhotoUpload } from "../PhotoUpload";
import { CityAutocomplete } from "../CityAutocomplete";
import { useT, useLabels } from "../../i18n";

const Radio = ({ active }) => (
  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${active ? "border-rox-black bg-rox-black" : "border-rox-off/60"}`}>
    {active && <Check size={12} className="text-rox-orange" strokeWidth={3} />}
  </span>
);

export const StepRace = ({ data, update, races = [] }) => {
  const { t, fmtDate } = useT();
  const L = useLabels();
  return (
    <>
      <h2 className="h-display text-4xl">{t("ob_race_h")}</h2>
      <p className="mt-2 text-sm text-rox-off/70">{t("ob_race_sub")}</p>
      <label className="mt-6 block">
        <span className="mb-2 block text-sm font-medium">{t("ob_city")}</span>
        <CityAutocomplete value={data.city} onSelect={update} testId="city-input" />
      </label>
      <p className="mt-6 mb-2 text-sm font-medium">{t("ob_race")}</p>
      <div className="space-y-2.5">
        {races.map((r) => (
          <button key={r.id} type="button" data-testid={`race-option-${r.id}`} data-active={data.race === r.id} className="option-card justify-between" onClick={() => update({ race: r.id })}>
            <span className="flex items-center gap-3"><Radio active={data.race === r.id} />{r.name}</span>
            {(r.date || r.id !== "other") && <span className={`font-body text-[11px] font-semibold normal-case tracking-normal ${data.race === r.id ? "text-rox-black/70" : "text-rox-muted"}`}>{r.date ? fmtDate(r.date) : t("race_date_tbc")}</span>}
          </button>
        ))}
      </div>
      <p className="mt-6 mb-2 text-sm font-medium">{t("ob_format")}</p>
      <div className="grid gap-2.5">
        {CATEGORIES.map((c) => (
          <button key={c.id} type="button" data-testid={`category-option-${c.id}`} data-active={data.category === c.id} className="option-card" onClick={() => update({ category: c.id, gender: c.id === "men" ? "male" : c.id === "women" ? "female" : data.gender })}>
            <Radio active={data.category === c.id} />{L.category({ category: c.id })}
          </button>
        ))}
      </div>
      {data.category === "mixed" && (
        <div className="mt-5 animate-rise-in">
          <span className="mb-2 block text-sm font-medium">{t("ob_you")}</span>
          <div className="grid grid-cols-2 gap-3">
            {["male", "female"].map((g) => (
              <button key={g} type="button" data-testid={`gender-option-${g}`} data-active={data.gender === g} className="option-card justify-center" onClick={() => update({ gender: g })}>{t(g)}</button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const StepLevel = ({ data, update }) => {
  const { t } = useT();
  const L = useLabels();
  return (
    <>
      <h2 className="h-display text-4xl">{t("ob_level_h")}</h2>
      <p className="mt-2 text-sm text-rox-off/70">{t("ob_level_sub")}</p>
      <div className="mt-6 space-y-3">
        {LEVELS.map((l) => {
          const active = data.level === l.id;
          return (
            <button key={l.id} type="button" data-testid={`level-option-${l.id}`} data-active={active} className="option-card flex-col !items-start gap-0.5" onClick={() => update({ level: l.id })}>
              <span>{L.level({ level: l.id })}</span>
              <span className={`font-body text-xs normal-case tracking-normal ${active ? "text-rox-black/70" : "text-rox-muted"}`}>{L.levelSub(l.id)}</span>
            </button>
          );
        })}
      </div>
    </>
  );
};

export const StepGoal = ({ data, update }) => {
  const { t } = useT();
  const L = useLabels();
  return (
    <>
      <h2 className="h-display text-4xl">{t("ob_goal_h")}</h2>
      <p className="mt-2 text-sm text-rox-off/70">{t("ob_goal_sub")}</p>
      <div className="mt-6 space-y-2.5">
        {GOALS.map((g) => (
          <button key={g.id} type="button" data-testid={`goal-option-${g.id}`} data-active={data.goal === g.id} className="option-card" onClick={() => update({ goal: g.id, target_time: g.time })}>
            <Radio active={data.goal === g.id} />{L.goal({ goal: g.id })}
          </button>
        ))}
      </div>
      <div className="panel mt-6 p-5">
        <p className="eyebrow">{t("target_time")}</p>
        <p className="h-display mt-1 text-6xl text-rox-orange" data-testid="target-time-display">{formatTime(Number(data.target_time))}</p>
        <input data-testid="target-time-slider" type="range" min={50} max={130} step={1} value={data.target_time} onChange={(e) => update({ target_time: e.target.value })} className="range-orange mt-5" />
        <div className="mt-2 flex justify-between text-[11px] uppercase tracking-wider text-rox-muted"><span>0h50</span><span>2h10</span></div>
      </div>
    </>
  );
};

export const StepStrengths = ({ data, update }) => {
  const { t } = useT();
  const L = useLabels();
  const toggle = (key) => update({ scores: { ...data.scores, [key]: data.scores[key] >= 7 ? 5 : 8 } });
  return (
    <>
      <h2 className="h-display text-4xl">{t("ob_strengths_h")}</h2>
      <p className="mt-2 text-sm text-rox-off/70">{t("ob_strengths_sub")}</p>
      <div className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {STATIONS.map((s) => (
          <button key={s.key} type="button" data-testid={`strength-${s.key}`} data-active={data.scores[s.key] >= 7} className="option-card justify-center text-center text-sm" onClick={() => toggle(s.key)}>
            {L.station(s.key)}
          </button>
        ))}
      </div>
    </>
  );
};

export const StepAvailability = ({ data, update }) => {
  const { t } = useT();
  const L = useLabels();
  const toggle = (id) => update({ availability: data.availability.includes(id) ? data.availability.filter((a) => a !== id) : [...data.availability, id] });
  const maxBirth = new Date(Date.now() - 16 * 365.25 * 86400000).toISOString().slice(0, 10);
  return (
    <>
      <h2 className="h-display text-4xl">{t("ob_avail_h")}</h2>
      <p className="mt-2 text-sm text-rox-off/70">{t("ob_avail_sub")}</p>
      <div className="mt-6 space-y-2.5">
        {AVAILABILITY.map((a) => (
          <button key={a.id} type="button" data-testid={`availability-option-${a.id}`} data-active={data.availability.includes(a.id)} className="option-card" onClick={() => toggle(a.id)}>
            <Radio active={data.availability.includes(a.id)} />{L.availabilityLabel(a.id)}
          </button>
        ))}
      </div>
      <label className="mt-8 block">
        <span className="mb-1 block text-sm font-medium">{t("birth_date")}</span>
        <span className="mb-2 block text-[11px] text-rox-muted">{t("birth_date_hint")}</span>
        <input data-testid="birth-date-input" className="field" type="date" max={maxBirth} min="1926-01-01" value={data.birth_date || ""} onChange={(e) => update({ birth_date: e.target.value || null })} />
      </label>
      <label className="mt-5 block">
        <span className="mb-2 block text-sm font-medium">{t("bio_label")}</span>
        <textarea data-testid="bio-input" className="field min-h-[96px] resize-none" maxLength={400} placeholder={t("bio_placeholder")} value={data.bio} onChange={(e) => update({ bio: e.target.value })} />
      </label>
      <label className="mt-5 block">
        <span className="mb-2 block text-sm font-medium">{t("instagram_label")}</span>
        <input data-testid="instagram-input" className="field" placeholder={t("ig_placeholder")} value={data.instagram} onChange={(e) => update({ instagram: e.target.value })} />
      </label>
      <div className="panel mt-5 p-3">
        <PhotoUpload name={data.first_name} photoUrl={data.photo_url} size={56} onUploaded={(url) => update({ photo_url: url })} />
      </div>
    </>
  );
};
